import * as THREE from 'three';
import fieldJson from '../../scenes/field.json';
import type { CompanionStatus } from '../companion/ScoutCompanion';
import { HudOverlay, type HudDisplayMode } from '../hud/HudOverlay';
import type { DisplayUnits } from '../hud/format';
import { buildHudModel, type HudViewModel } from '../hud/hudModel';
import { LapTimer } from '../hud/lapTimer';
import { CalibrationScreen } from '../input/CalibrationScreen';
import { CalibrationWizard } from '../input/calibration';
import { DEFAULT_INPUT_PROFILE, type InputProfile } from '../input/channels';
import { GamepadSource, hashGamepadId, type GetGamepads } from '../input/gamepad';
import { InputRouter } from '../input/InputRouter';
import { KeyboardSource, type KeyEventLike } from '../input/keyboard';
import { ProfileStore, resolveStartupProfile } from '../input/profiles';
import { applyRenderQuality, type RenderQuality } from '../render/RenderQuality';
import { CameraRig, type CameraMode } from '../render/cameras/CameraRig';
import { buildPostFx, postFxConfigFor, type PostFxComposer } from '../render/cameras/postFx';
import { ScoutObservationCamera } from '../render/cameras/ScoutObservationCamera';
import { EntityLayer } from '../render/entities/EntityLayer';
import { RadarViz, type RadarVizLayer } from '../render/radarViz/RadarViz';
import { buildScene, type SceneBundle } from '../render/scene/buildScene';
import { parseFieldScene } from '../scene/fieldSchema';
import { MetricsAccumulator, type MetricsResult } from '../sim/metrics';
import { distance } from '../sim/mathx';
import { parseScenario, type ScenarioSpec } from '../sim/scenarioSchema';
import { DEFAULT_RCS_BY_CLASS, type TargetState } from '../sim/targetTypes';
import type { Track } from '../sim/trackTypes';
import { interpolateTelemetry } from '../state/interpolate';
import type { Command } from '../state/commandRing';
import type { TelemetrySnapshot } from '../state/telemetryBuffer';
import { SimWorkerClient, type FatalListener } from './simWorkerClient';
import { UiStateStore, type ControlMode, type UiState } from './uiState';
import './app.css';

export const DEFAULT_SCENARIO_NAME = 'radar-cued-observation';

export type RafScheduler = (callback: (nowMs: number) => void) => number;

export interface AppShellOptions {
  document: Document;
  storage: Storage;
  workerClientFactory: (scenario: unknown) => SimWorkerClient;
  scenarios: Record<string, unknown>;
  rafScheduler?: RafScheduler;
  now?: () => number;
  getGamepads?: GetGamepads;
  renderer?: THREE.WebGLRenderer | null;
}

export const APP_SHORTCUTS = Object.freeze({
  Space: 'Pause / resume (arm / disarm in manual control mode)',
  Digit1: 'FPV camera',
  Digit2: 'Chase camera',
  Digit3: 'Orbit camera',
  Digit4: 'Radar-eye camera',
  KeyH: 'Cycle HUD mode',
  KeyR: 'Reset scenario',
  KeyC: 'Open calibration',
  Escape: 'Close calibration',
  Slash: 'Show / hide shortcuts',
});

type ShortcutCode = keyof typeof APP_SHORTCUTS;

interface ShellControls {
  scenario: HTMLSelectElement;
  pause: HTMLButtonElement;
  timeScale: HTMLInputElement;
  camera: HTMLSelectElement;
  quality: HTMLSelectElement;
  hud: HTMLSelectElement;
  control: HTMLSelectElement;
  units: HTMLSelectElement;
  radarLayers: Record<RadarVizLayer, HTMLInputElement>;
  metrics: HTMLElement;
  shortcuts: HTMLElement;
}

const field = parseFieldScene(fieldJson);
const startGateId = field.gates[0]!.id;
const finishGateId = field.gates.at(-1)!.id;

const addClass = <T extends HTMLElement>(element: T, ...classes: string[]): T => {
  element.classList.add(...classes);
  return element;
};

const toggleClass = (element: HTMLElement, className: string, on: boolean): void => {
  const classList = element.classList as DOMTokenList & {
    toggle?: (token: string, force?: boolean) => boolean;
    remove?: (...tokens: string[]) => void;
  };
  if (classList.toggle !== undefined) classList.toggle(className, on);
  else if (on) classList.add(className);
  else classList.remove?.(className);
};

const releaseManualCommand = (): Command => ({
  roll: 0,
  pitch: 0,
  yaw: 0,
  throttle: 0,
  mode: 'auto',
  arm: false,
  disarm: false,
  reset: false,
  releaseManual: true,
  tuning: [],
});

const targetStatesFrom = (snapshot: TelemetrySnapshot, scenario: ScenarioSpec): TargetState[] => {
  const specifications = new Map(scenario.targets.map((target) => [target.id, target]));
  return snapshot.targets.map((target) => {
    const specification = specifications.get(target.id);
    const targetClass = specification?.class ?? 'drone';
    return {
      id: target.id,
      posEnu: { ...target.posEnu },
      velEnu: { x: 0, y: 0, z: 0 },
      headingRad: 0,
      class: targetClass,
      rcs: specification?.rcs ?? DEFAULT_RCS_BY_CLASS[targetClass],
    };
  });
};

const tracksFrom = (snapshot: TelemetrySnapshot): Track[] =>
  snapshot.tracks.map((track) => {
    let truthId: string | undefined;
    let closestDistance = Infinity;
    for (const target of snapshot.targets) {
      const separation = distance(track.posEnu, target.posEnu);
      if (separation < closestDistance) {
        closestDistance = separation;
        truthId = target.id;
      }
    }
    return {
      ...track,
      covPos: 0,
      firstSeen: 0,
      lastUpdate: snapshot.time,
      hitCount: 0,
      missCount: 0,
      assocTruthId: truthId,
    };
  });

/** Testable application composition root. Browser globals enter only through options. */
export class AppShell {
  readonly root: HTMLElement;
  readonly workerClient: SimWorkerClient;
  readonly sceneBundle: SceneBundle;
  readonly hud: HudOverlay;
  readonly observationCamera = new ScoutObservationCamera();

  entityLayer: EntityLayer;
  radarViz: RadarViz;
  cameraRig: CameraRig;
  inputRouter: InputRouter;
  calibrationScreen: CalibrationScreen | null = null;
  lastHudViewModel: HudViewModel | null = null;
  isPaused = false;

  private readonly document: Document;
  private readonly scenarios: Record<string, unknown>;
  private readonly renderer: THREE.WebGLRenderer | null;
  private readonly uiStore: UiStateStore;
  private readonly profileStore: ProfileStore;
  private readonly getGamepads: GetGamepads;
  private readonly gamepadSource: GamepadSource;
  private readonly keyboardSource = new KeyboardSource();
  private readonly now: () => number;
  private readonly rafScheduler?: RafScheduler;
  private readonly controls: ShellControls;
  private scenarioName = DEFAULT_SCENARIO_NAME;
  private scenario: ScenarioSpec;
  private lapTimer: LapTimer;
  private metricsAccumulator: MetricsAccumulator;
  private previousTelemetry: TelemetrySnapshot | null = null;
  private latestTelemetry: TelemetrySnapshot | null = null;
  private latestTelemetryWallMs = 0;
  private lastTickMs: number | null = null;
  private lastAccumulatedTime: number | null = null;
  private lastAccumulatedChecksum: number | null = null;
  private timeScale = 1;
  private postFx: PostFxComposer | undefined;
  private calibrationWizard: CalibrationWizard | null = null;
  private disposed = false;

  /**
   * Space is double-booked: the global shortcut pauses/resumes, while the
   * keyboard flight profile uses it as the arm/disarm switch. In manual
   * control mode arming is the safety-relevant action, so route Space to
   * the flight stick instead of stealing it for the global shortcut.
   */
  private isFlightControlKey(code: string): boolean {
    return code === 'Space' && this.uiState.controlMode === 'manual';
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.code in APP_SHORTCUTS && !this.isFlightControlKey(event.code)) {
      event.preventDefault?.();
      this.handleShortcut(event.code);
      return;
    }
    this.keyboardSource.handleKey(event as KeyEventLike);
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    if (!(event.code in APP_SHORTCUTS) || this.isFlightControlKey(event.code)) {
      this.keyboardSource.handleKey(event as KeyEventLike);
    }
  };

  constructor(options: AppShellOptions) {
    this.document = options.document;
    this.scenarios = options.scenarios;
    this.renderer = options.renderer ?? null;
    this.now = options.now ?? (() => performance.now());
    this.rafScheduler = options.rafScheduler;
    this.getGamepads = options.getGamepads ?? (() => []);
    this.uiStore = new UiStateStore(options.storage);
    this.profileStore = new ProfileStore(options.storage);

    const source = this.scenarios[DEFAULT_SCENARIO_NAME];
    if (source === undefined) {
      throw new Error(`Default scenario is missing: ${DEFAULT_SCENARIO_NAME}`);
    }
    this.scenario = parseScenario(source);
    this.workerClient = options.workerClientFactory(source);
    this.sceneBundle = buildScene(this.scenario.scene.id, this.uiState.renderQuality);
    this.entityLayer = this.buildEntityLayer(this.scenario);
    this.radarViz = new RadarViz(this.sceneBundle.scene, this.scenario.radar);
    this.cameraRig = new CameraRig(this.scenario.radar.mountEnu);
    this.hud = new HudOverlay(this.document);
    this.gamepadSource = new GamepadSource(this.getGamepads);
    this.inputRouter = this.buildInputRouter(this.startupProfile());
    this.lapTimer = this.newLapTimer();
    this.metricsAccumulator = new MetricsAccumulator(this.scenario);

    const built = this.buildRoot();
    this.root = built.root;
    this.controls = built.controls;
    this.root.appendChild(this.hud.root);
    if (this.renderer !== null)
      this.root.insertBefore(this.renderer.domElement, this.root.firstChild);

    this.applyRestoredPreferences();
    this.installKeyboardListeners();
    this.rebuildPostFx();
    if (this.rafScheduler !== undefined) this.scheduleFrame();
  }

  get uiState(): UiState {
    return this.uiStore.state;
  }

  loadScenario(name: string): void {
    this.assertAlive();
    const source = this.scenarios[name];
    if (source === undefined) throw new Error(`Unknown scenario: ${name}`);
    const scenario = parseScenario(source);
    if (scenario.scene.id !== this.scenario.scene.id) {
      throw new Error(
        `Scenario scene ${scenario.scene.id} cannot replace active scene ${this.scenario.scene.id}`,
      );
    }

    const nextEntities = this.buildEntityLayer(scenario);
    const nextRadarViz = new RadarViz(this.sceneBundle.scene, scenario.radar);
    const nextCameraRig = new CameraRig(scenario.radar.mountEnu);
    nextCameraRig.setMode(this.uiState.cameraMode);

    this.entityLayer.dispose();
    this.radarViz.dispose();
    this.entityLayer = nextEntities;
    this.radarViz = nextRadarViz;
    this.cameraRig = nextCameraRig;
    this.scenario = scenario;
    this.scenarioName = name;
    this.controls.scenario.value = name;
    this.isPaused = false;
    this.controls.pause.textContent = 'Pause';
    this.resetRunState();
    this.applyRadarLayers();
    this.workerClient.loadScenario(source);
    this.rebuildPostFx();
  }

  reset(): void {
    this.assertAlive();
    this.workerClient.reset();
    this.resetRunState();
  }

  tick(nowMs: number = this.now()): void {
    this.assertAlive();
    if (!Number.isFinite(nowMs)) throw new RangeError('tick nowMs must be finite');
    const dt = this.lastTickMs === null ? 0 : Math.max(0, (nowMs - this.lastTickMs) / 1_000);
    this.lastTickMs = nowMs;

    if (this.uiState.controlMode === 'manual') this.inputRouter.frame(dt);
    this.updateCalibration(nowMs);

    const snapshot = this.workerClient.readTelemetry();
    if (snapshot === null) return;
    this.acceptTelemetry(snapshot, nowMs);
    const latest = this.latestTelemetry!;
    const previous = this.previousTelemetry ?? latest;
    const renderTime = this.isPaused
      ? latest.time
      : latest.time + Math.max(0, (nowMs - this.latestTelemetryWallMs) / 1_000) * this.timeScale;
    const interp = interpolateTelemetry(previous, latest, renderTime);

    this.entityLayer.update(interp, latest, dt);
    this.radarViz.update(latest, interp, interp.scout.posEnu, dt);
    const selectedTrack = interp.tracks.find(
      (track) => track.id === latest.companion.selectedTrackId,
    );
    this.cameraRig.update(
      interp,
      {
        scoutVelEnu: latest.scout.velEnu,
        selectedTrackEnu: selectedTrack?.posEnu ?? null,
      },
      dt,
    );
    this.observationCamera.update(interp.scout);
    this.accumulate(latest);

    const companion = this.companionStatus(latest);
    const result = this.metricsAccumulator.result();
    this.lastHudViewModel = buildHudModel({
      interp,
      telemetry: latest,
      companion,
      units: this.uiState.displayUnits,
      throttleScale: this.batteryScale(latest),
      classForTrack: (id) => this.classForTrack(id, latest),
      lap: this.lapTimer.state(),
      minSeparationM: result.minSeparationM,
      groundElevationM: field.ground.z,
      originAltitudeMslM: this.scenario.originLatLonAlt.alt,
    });
    this.hud.apply(this.lastHudViewModel);
    this.controls.metrics.textContent = this.formatMetrics(result);
    this.render(latest);
  }

  pause(): void {
    this.assertAlive();
    if (this.isPaused) return;
    this.isPaused = true;
    this.controls.pause.textContent = 'Resume';
    this.workerClient.pause();
  }

  resume(): void {
    this.assertAlive();
    if (!this.isPaused) return;
    this.isPaused = false;
    this.controls.pause.textContent = 'Pause';
    this.latestTelemetryWallMs = this.now();
    this.workerClient.resume();
  }

  stepOneSecond(): void {
    this.assertAlive();
    this.workerClient.stepSeconds(1);
  }

  setTimeScale(timeScale: number): void {
    this.assertAlive();
    if (!Number.isFinite(timeScale) || timeScale < 0) {
      throw new RangeError('timeScale must be a finite non-negative number');
    }
    this.timeScale = timeScale;
    this.controls.timeScale.value = String(timeScale);
    this.workerClient.setTimeScale(timeScale);
  }

  setControlMode(mode: ControlMode): void {
    this.assertAlive();
    this.uiStore.update({ controlMode: mode });
    this.controls.control.value = mode;
    toggleClass(this.root, 'is-manual', mode === 'manual');
    if (mode === 'auto') this.workerClient.commandRing.push(releaseManualCommand());
  }

  setCameraMode(mode: CameraMode): void {
    this.assertAlive();
    this.uiStore.update({ cameraMode: mode });
    this.controls.camera.value = mode;
    this.cameraRig.setMode(mode);
    this.rebuildPostFx();
  }

  setRenderQuality(quality: RenderQuality): void {
    this.assertAlive();
    this.uiStore.update({ renderQuality: quality });
    this.controls.quality.value = quality;
    applyRenderQuality(this.sceneBundle, quality);
    this.rebuildPostFx();
  }

  setHudMode(mode: HudDisplayMode): void {
    this.assertAlive();
    this.uiStore.update({ hudMode: mode });
    this.controls.hud.value = mode;
    this.hud.setDisplayMode(mode);
  }

  setRadarLayer(layer: RadarVizLayer, on: boolean): void {
    this.assertAlive();
    this.uiStore.update({ radarLayers: { [layer]: on } });
    this.controls.radarLayers[layer].checked = on;
    this.radarViz.setLayerVisible(layer, on);
  }

  setDisplayUnits(units: DisplayUnits): void {
    this.assertAlive();
    this.uiStore.update({ displayUnits: units });
    this.controls.units.value = `${units.length}/${units.speed}`;
  }

  openCalibration(): void {
    this.assertAlive();
    if (this.calibrationScreen !== null) return;
    const pad = this.getGamepads().find((candidate) => candidate?.connected !== false) ?? null;
    const deviceName = pad?.id ?? 'Generic controller';
    const deviceHash = pad === null ? 'generic-gamepad' : hashGamepadId(pad.id);
    this.calibrationWizard = new CalibrationWizard({
      deviceHash,
      deviceName,
      axisCount: Math.max(4, pad?.axes.length ?? 0),
      buttonCount: Math.max(4, pad?.buttons.length ?? 0),
    });
    this.calibrationScreen = new CalibrationScreen(this.calibrationWizard, {
      document: this.document,
      onSave: (stored) => {
        this.profileStore.save(stored);
        this.inputRouter = this.buildInputRouter(stored.profile);
        this.closeCalibration();
      },
    });
    this.root.appendChild(this.calibrationScreen.root);
    this.root.classList.add('calibration-open');
  }

  closeCalibration(): void {
    if (this.calibrationScreen === null) return;
    if (this.calibrationScreen.root.parentElement === this.root) {
      this.root.removeChild(this.calibrationScreen.root);
    }
    this.calibrationScreen = null;
    this.calibrationWizard = null;
    toggleClass(this.root, 'calibration-open', false);
  }

  onFatal(listener: FatalListener): () => void {
    return this.workerClient.onFatal(listener);
  }

  keyboardShortcuts(): Readonly<Record<string, string>> {
    return { ...APP_SHORTCUTS };
  }

  handleShortcut(code: string): boolean {
    if (!(code in APP_SHORTCUTS)) return false;
    switch (code as ShortcutCode) {
      case 'Space':
        if (this.isPaused) this.resume();
        else this.pause();
        break;
      case 'Digit1':
        this.setCameraMode('fpv');
        break;
      case 'Digit2':
        this.setCameraMode('chase');
        break;
      case 'Digit3':
        this.setCameraMode('orbit');
        break;
      case 'Digit4':
        this.setCameraMode('radar-eye');
        break;
      case 'KeyH':
        this.setHudMode(this.nextHudMode());
        break;
      case 'KeyR':
        this.reset();
        break;
      case 'KeyC':
        this.openCalibration();
        break;
      case 'Escape':
        this.closeCalibration();
        break;
      case 'Slash':
        this.controls.shortcuts.hidden = !this.controls.shortcuts.hidden;
        break;
    }
    return true;
  }

  metrics(): MetricsResult {
    return this.metricsAccumulator.result();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.closeCalibration();
    this.removeKeyboardListeners();
    this.postFx?.dispose();
    this.postFx = undefined;
    this.entityLayer.dispose();
    this.radarViz.dispose();
    this.sceneBundle.dispose();
    this.workerClient.dispose();
    this.renderer?.dispose();
  }

  private buildEntityLayer(scenario: ScenarioSpec): EntityLayer {
    return new EntityLayer(this.sceneBundle.scene, {
      radarEnu: scenario.radar.mountEnu,
      homeEnu: scenario.scout.homeEnu,
      classById: new Map(scenario.targets.map((target) => [target.id, target.class])),
    });
  }

  private buildInputRouter(profile: InputProfile): InputRouter {
    return new InputRouter({
      commandRing: this.workerClient.commandRing,
      gamepadSource: this.gamepadSource,
      keyboardSource: this.keyboardSource,
      profile,
    });
  }

  private startupProfile(): InputProfile {
    const pad = this.getGamepads().find((candidate) => candidate?.connected !== false) ?? null;
    if (pad === null) return DEFAULT_INPUT_PROFILE;
    return resolveStartupProfile(hashGamepadId(pad.id), this.profileStore).profile;
  }

  private newLapTimer(): LapTimer {
    return new LapTimer(field.gates, { startGateId, finishGateId });
  }

  private resetRunState(): void {
    this.previousTelemetry = null;
    this.latestTelemetry = null;
    this.lastAccumulatedTime = null;
    this.lastAccumulatedChecksum = null;
    this.lastHudViewModel = null;
    this.lapTimer = this.newLapTimer();
    this.metricsAccumulator = new MetricsAccumulator(this.scenario);
    this.controls.metrics.textContent = '';
  }

  private acceptTelemetry(snapshot: TelemetrySnapshot, nowMs: number): void {
    const changed =
      this.latestTelemetry === null || snapshot.checksum !== this.latestTelemetry.checksum;
    if (!changed) return;
    if (this.latestTelemetry !== null && snapshot.time >= this.latestTelemetry.time) {
      this.previousTelemetry = this.latestTelemetry;
    } else {
      this.previousTelemetry = snapshot;
    }
    this.latestTelemetry = snapshot;
    this.latestTelemetryWallMs = nowMs;
  }

  private accumulate(snapshot: TelemetrySnapshot): void {
    if (snapshot.checksum === this.lastAccumulatedChecksum) return;
    if (this.lastAccumulatedTime !== null && snapshot.time < this.lastAccumulatedTime) {
      this.lapTimer = this.newLapTimer();
      this.metricsAccumulator = new MetricsAccumulator(this.scenario);
    }
    this.lapTimer.update(snapshot.scout.posEnu, snapshot.time);
    this.metricsAccumulator.addTick(
      {
        time: snapshot.time,
        scout: {
          posEnu: snapshot.scout.posEnu,
          attitude: snapshot.scout.attitude,
        },
        targets: targetStatesFrom(snapshot, this.scenario),
      },
      tracksFrom(snapshot),
      this.companionStatus(snapshot),
    );
    this.lastAccumulatedTime = snapshot.time;
    this.lastAccumulatedChecksum = snapshot.checksum;
  }

  private companionStatus(snapshot: TelemetrySnapshot): CompanionStatus {
    const selected = snapshot.tracks.find(
      (track) => track.id === snapshot.companion.selectedTrackId,
    );
    const range = selected === undefined ? null : distance(snapshot.scout.posEnu, selected.posEnu);
    return {
      ...snapshot.companion,
      targetPosition: selected?.posEnu ?? null,
      rangeToTarget: range,
      timeToStation: range === null ? null : range / 8,
      effectiveRadius: this.scenario.companion.standoffRadius,
      separationProtectionActive: false,
    };
  }

  private classForTrack(id: number, snapshot: TelemetrySnapshot): string {
    const track = snapshot.tracks.find((candidate) => candidate.id === id);
    if (track === undefined) return 'unknown';
    let closest = snapshot.targets[0];
    for (const target of snapshot.targets) {
      if (
        closest === undefined ||
        distance(track.posEnu, target.posEnu) < distance(track.posEnu, closest.posEnu)
      ) {
        closest = target;
      }
    }
    return this.scenario.targets.find((target) => target.id === closest?.id)?.class ?? 'unknown';
  }

  private batteryScale(snapshot: TelemetrySnapshot): number {
    const battery = this.scenario.scout.vehicleConfig.battery;
    return snapshot.scout.batteryV / (battery.cells * battery.restingVoltagePerCell);
  }

  private formatMetrics(metrics: MetricsResult): string {
    const finite = (value: number, unit: string): string =>
      Number.isFinite(value) ? `${value.toFixed(1)}${unit}` : '--';
    return [
      `reaction ${finite(metrics.reactionTimeS, 's')}`,
      `station ${finite(metrics.stationErrorM, 'm')}`,
      `view ${finite(metrics.targetInFrustumPct, '%')}`,
      `separation ${finite(metrics.minSeparationM, 'm')}`,
      `continuity ${finite(metrics.trackContinuityPct, '%')}`,
    ].join(' · ');
  }

  private render(snapshot: TelemetrySnapshot): void {
    if (this.renderer === null) return;
    const speed = Math.hypot(
      snapshot.scout.velEnu.x,
      snapshot.scout.velEnu.y,
      snapshot.scout.velEnu.z,
    );
    if (this.postFx !== undefined) {
      this.postFx.setSpeedMps(speed);
      this.postFx.render();
    } else {
      this.renderer.render(this.sceneBundle.scene, this.cameraRig.activeCamera());
    }
  }

  private rebuildPostFx(): void {
    this.postFx?.dispose();
    this.postFx = buildPostFx(
      this.renderer,
      this.sceneBundle.scene,
      this.cameraRig.activeCamera(),
      postFxConfigFor(this.uiState.renderQuality),
    );
  }

  private applyRestoredPreferences(): void {
    const state = this.uiState;
    this.cameraRig.setMode(state.cameraMode);
    this.hud.setDisplayMode(state.hudMode);
    applyRenderQuality(this.sceneBundle, state.renderQuality);
    this.controls.scenario.value = this.scenarioName;
    this.controls.camera.value = state.cameraMode;
    this.controls.quality.value = state.renderQuality;
    this.controls.hud.value = state.hudMode;
    this.controls.control.value = state.controlMode;
    this.controls.units.value = `${state.displayUnits.length}/${state.displayUnits.speed}`;
    toggleClass(this.root, 'is-manual', state.controlMode === 'manual');
    this.applyRadarLayers();
  }

  private applyRadarLayers(): void {
    const layers = this.uiState.radarLayers;
    (Object.keys(layers) as RadarVizLayer[]).forEach((layer) => {
      this.controls.radarLayers[layer].checked = layers[layer];
      this.radarViz.setLayerVisible(layer, layers[layer]);
    });
  }

  private updateCalibration(nowMs: number): void {
    if (this.calibrationScreen === null || this.calibrationWizard === null) return;
    const pad = this.getGamepads().find((candidate) => candidate?.connected !== false) ?? null;
    const frame = {
      axes: Array.from(pad?.axes ?? []),
      buttons: Array.from(pad?.buttons ?? [], (button) => button.value),
    };
    this.calibrationWizard.ingest(frame, nowMs);
    this.calibrationScreen.render(frame);
  }

  private nextHudMode(): HudDisplayMode {
    const modes: readonly HudDisplayMode[] = ['full', 'compact', 'cinematic'];
    return modes[(modes.indexOf(this.uiState.hudMode) + 1) % modes.length]!;
  }

  private scheduleFrame(): void {
    this.rafScheduler?.((nowMs) => {
      if (this.disposed) return;
      this.tick(nowMs);
      this.scheduleFrame();
    });
  }

  private installKeyboardListeners(): void {
    this.document.addEventListener?.('keydown', this.onKeyDown);
    this.document.addEventListener?.('keyup', this.onKeyUp);
  }

  private removeKeyboardListeners(): void {
    this.document.removeEventListener?.('keydown', this.onKeyDown);
    this.document.removeEventListener?.('keyup', this.onKeyUp);
  }

  private buildRoot(): { root: HTMLElement; controls: ShellControls } {
    const root = addClass(this.document.createElement('main'), 'app-shell');
    const toolbar = addClass(this.document.createElement('nav'), 'app-toolbar');
    const scenario = this.select(Object.keys(this.scenarios));
    const load = this.button('Load', () => this.loadScenario(scenario.value));
    const reset = this.button('Reset', () => this.reset());
    const control = this.select(['auto', 'manual']);
    control.addEventListener('change', () => this.setControlMode(control.value as ControlMode));
    const pause = this.button('Pause', () => {
      if (this.isPaused) this.resume();
      else this.pause();
    });
    const step = this.button('Step 1s', () => this.stepOneSecond());
    const timeScale = this.document.createElement('input');
    timeScale.type = 'range';
    timeScale.min = '0.1';
    timeScale.max = '2';
    timeScale.step = '0.05';
    timeScale.value = '1';
    timeScale.addEventListener('input', () => this.setTimeScale(Number(timeScale.value)));
    const camera = this.select(['fpv', 'chase', 'orbit', 'radar-eye']);
    camera.addEventListener('change', () => this.setCameraMode(camera.value as CameraMode));
    const quality = this.select(['low', 'med', 'high']);
    quality.addEventListener('change', () => this.setRenderQuality(quality.value as RenderQuality));
    const hud = this.select(['full', 'compact', 'cinematic']);
    hud.addEventListener('change', () => this.setHudMode(hud.value as HudDisplayMode));
    const units = this.select(['m/mps', 'm/kmh', 'ft/mps', 'ft/kmh']);
    units.addEventListener('change', () => {
      const [length, speed] = units.value.split('/');
      this.setDisplayUnits({
        length: length as DisplayUnits['length'],
        speed: speed as DisplayUnits['speed'],
      });
    });
    const radarLayers = Object.fromEntries(
      (['coverage', 'sweep', 'tracks', 'halo', 'ppi'] as const).map((layer) => {
        const label = addClass(this.document.createElement('label'), 'app-radar-toggle');
        const input = this.document.createElement('input');
        input.type = 'checkbox';
        input.addEventListener('change', () => this.setRadarLayer(layer, input.checked));
        label.appendChild(input);
        const text = this.document.createElement('span');
        text.textContent = layer;
        label.appendChild(text);
        toolbar.appendChild(label);
        return [layer, input];
      }),
    ) as Record<RadarVizLayer, HTMLInputElement>;
    const calibrate = this.button('Calibrate', () => this.openCalibration());
    const help = this.button('?', () => this.handleShortcut('Slash'));

    [
      scenario,
      load,
      reset,
      control,
      pause,
      step,
      timeScale,
      camera,
      quality,
      hud,
      units,
      calibrate,
      help,
    ].forEach((element) => toolbar.appendChild(element));
    root.appendChild(toolbar);

    const metricsPanel = addClass(this.document.createElement('details'), 'app-metrics-panel');
    const metricsSummary = this.document.createElement('summary');
    metricsSummary.textContent = 'Metrics';
    const metrics = addClass(this.document.createElement('output'), 'app-metrics');
    metricsPanel.appendChild(metricsSummary);
    metricsPanel.appendChild(metrics);
    root.appendChild(metricsPanel);
    const shortcuts = addClass(this.document.createElement('aside'), 'app-shortcuts');
    shortcuts.hidden = true;
    shortcuts.textContent = Object.entries(APP_SHORTCUTS)
      .map(([code, description]) => `${code}: ${description}`)
      .join(' · ');
    root.appendChild(shortcuts);
    return {
      root,
      controls: {
        scenario,
        pause,
        timeScale,
        camera,
        quality,
        hud,
        control,
        units,
        radarLayers,
        metrics,
        shortcuts,
      },
    };
  }

  private select(values: readonly string[]): HTMLSelectElement {
    const select = this.document.createElement('select');
    values.forEach((value) => {
      const option = this.document.createElement('option');
      option.value = value;
      option.textContent = value;
      select.appendChild(option);
    });
    return select;
  }

  private button(label: string, action: () => void): HTMLButtonElement {
    const button = this.document.createElement('button');
    button.type = 'button';
    button.textContent = label;
    button.addEventListener('click', action);
    return button;
  }

  private assertAlive(): void {
    if (this.disposed) throw new Error('AppShell is disposed');
  }
}
